import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import { ProjectGroup } from 'library/types';
import { useAppSelector } from 'store/hooks';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateProjectGroupDialog, UpdateProjectGroupDialog } from './components';

const ProjectGroups = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProjectGroup[]>([]);
  const [openDeleteProjectGroup, setOpenDeleteProjectGroup] = useState(false);
  const [openCreateProjectGroup, setOpenCreateProjectGroup] = useState(false);
  const [openUpdateProjectGroup, setOpenUpdateProjectGroup] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projectGroups.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getProjectGroups();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleCloseDeleteProjectGroup = () => setOpenDeleteProjectGroup(false);
  const handleClickOpenDeleteProjectGroup = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteProjectGroup(true);
  };

  const handleCloseCreateProjectGroup = () => setOpenCreateProjectGroup(false);
  const handleClickOpenCreateProjectGroup = () => setOpenCreateProjectGroup(true);

  const handleCloseUpdateProjectGroup = () => setOpenUpdateProjectGroup(false);
  const handleClickOpenUpdateProjectGroup = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateProjectGroup(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-product-group')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-product-group']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateProjectGroup}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="projectGroups.createProjectGroup" defaultMessage="Ürün Grubu Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-product-group']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateProjectGroup}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="projectGroups.updateProjectGroup" defaultMessage="Ürün Grubu Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-product-group']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteProjectGroup}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="projectGroups.deleteProjectGroup" defaultMessage="Ürün Grubu Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateProjectGroupDialog
        open={openCreateProjectGroup}
        handleClose={handleCloseCreateProjectGroup}
        refresh={getValues}
      />
      <UpdateProjectGroupDialog
        open={openUpdateProjectGroup}
        projectGroupId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateProjectGroup}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteProjectGroup}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProjectGroup}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="projectGroups.deleteProjectGroup" defaultMessage="Ürün Grubu Sil" />}
        subTitle={
          <FormattedMessage
            id="projectGroups.deleteProjectGroupRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra ürün grubu veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteProjectGroup}
        successMessage={
          <FormattedMessage
            id="projectGroups.deleteProjectGroupCompleted"
            defaultMessage="Seçili ürün grubunu silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ProjectGroups;
